import { claimDailyGift } from '@bonanza-social/daily-gift';
import {
  loyaltyPointsUpdated,
  selectLoyaltyLevel,
} from '@bonanza-social/levels';
import {
  isAnyOf,
  socketConnected,
  startListening,
} from '@bonanza-social/toolkit/state';
import { getCoinBalance, selectCoinBalance } from '@bonanza-social/wallet';
import { Notification, RewardType } from '@bonanza-social/rewards';
import { BottomSheet } from '@bonanza-social/navigation';
import {
  claimStatusReward,
  claimAllStatusRewards,
  statusBoxClaimed,
  statusChanged,
} from '@bonanza-social/status';
import { openShopRewardsBottomSheet } from '@bonanza-social/shop/state';
import {
  fetchDailyRewards,
  selectIsAnyDailyRewardWithPositiveRewardAmount,
} from '@bonanza-social/daily-rewards/state';
import { instantMetaGameCompleted } from '@bonanza-social/meta-games';
import { prizeWallCoinsRewardWon } from '@bonanza-social/prize-wall/state';
import { SecureStorage } from '@bonanza-social/toolkit/storage';

startListening({
  matcher: isAnyOf(
    claimDailyGift.fulfilled,
    claimStatusReward.fulfilled,
    claimAllStatusRewards.fulfilled,
    openShopRewardsBottomSheet,
    statusBoxClaimed,
    statusChanged,
    instantMetaGameCompleted,
    prizeWallCoinsRewardWon,
  ),
  effect: (_, { dispatch }) => {
    dispatch(getCoinBalance());
  },
});

startListening({
  actionCreator: loyaltyPointsUpdated,
  effect: (action, { dispatch }) => {
    const hasCoinReward = action.payload.loyaltyRewards.some(
      (reward) => reward.rewardType === 'Coins',
    );

    if (hasCoinReward) {
      dispatch(getCoinBalance());
    }
  },
});

startListening({
  actionCreator: fetchDailyRewards.fulfilled,
  effect: (_, { dispatch, getState }) => {
    const hasRewardAmount =
      selectIsAnyDailyRewardWithPositiveRewardAmount(getState());

    if (hasRewardAmount) {
      dispatch(getCoinBalance());
    }
  },
});

startListening({
  actionCreator: socketConnected,
  effect: (action, { dispatch }) => {
    const socketConnection = action.payload;

    socketConnection.on(
      'RewardMessageReceived',
      (notification: Notification) => {
        // Skip the fetch here becase we fetch when openShopRewardsBottomSheet is fired
        if (notification.sourceType === 'ItemPurchase') {
          return;
        }

        if (
          notification.rewards.some(
            (reward) => reward.rewardType === RewardType.Coins,
          )
        ) {
          dispatch(getCoinBalance());
        }
      },
    );
  },
});

startListening({
  actionCreator: getCoinBalance.fulfilled,
  effect: async (_, { getState }) => {
    const state = getState();

    const coinBalance = selectCoinBalance(state);
    const { level } = selectLoyaltyLevel(state) ?? { level: 0 };

    const storedValue = await SecureStorage.getItem('lowCoinBalanceAlertShown');
    let lowCoinBalanceAlertShown = false;

    if (storedValue) {
      try {
        const parsed = JSON.parse(storedValue);
        const timestamp = parsed.timestamp;
        const now = Date.now();
        const twentyFourHours = 24 * 60 * 60 * 1000;

        // Show message again only if 24 hours have passed
        if (now - timestamp < twentyFourHours) {
          lowCoinBalanceAlertShown = parsed.shown === true;
        } else {
          await SecureStorage.removeItem('lowCoinBalanceAlertShown');
        }
      } catch {
        await SecureStorage.removeItem('lowCoinBalanceAlertShown');
      }
    }

    const shouldShowLowCoinBalanceAlert =
      coinBalance < 1000 && level > 1 && !lowCoinBalanceAlertShown;

    if (shouldShowLowCoinBalanceAlert) {
      await SecureStorage.setItem(
        'lowCoinBalanceAlertShown',
        JSON.stringify({ shown: true, timestamp: Date.now() }),
      );

      BottomSheet.open({
        namespace: 'wallet',
        type: 'LowCoinBalance',
      });
    }
  },
});
